import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Calendar, Clock, MapPin, Users, CheckCircle, Play, QrCode } from 'lucide-react';

const NextClassCard = ({ session, classData, onMarkAttendance, onQRAttendance }) => {
  const { theme } = useTheme();

  if (!session) return null;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    }
  };

  const isToday = () => {
    const sessionDate = new Date(session.date).toDateString();
    const today = new Date().toDateString();
    return sessionDate === today;
  };

  const getTimeUntilSession = () => {
    const now = new Date();
    const sessionDateTime = new Date(`${session.date}T${session.time}`);
    const diffMs = sessionDateTime - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffMs < 0) return 'In progress';
    if (diffHours < 1) return `In ${diffMins} minutes`;
    if (diffHours < 24) return `In ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    return null;
  };

  const timeUntil = getTimeUntilSession();

  return (
    <div className={`${theme.card} rounded-xl border-2 ${isToday() ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : theme.border} p-6 mb-6`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${isToday() ? 'bg-blue-500 text-white' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'}`}>
            <Calendar size={24} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className={`text-xl font-bold ${theme.text}`}>
                {isToday() ? 'Today\'s Class' : 'Next Class'}
              </h3>
              {isToday() && (
                <span className="px-2 py-1 bg-blue-500 text-white rounded text-xs font-semibold animate-pulse">
                  LIVE
                </span>
              )}
            </div>
            <p className={`text-lg ${theme.textSecondary}`}>
              {formatDate(session.date)}
            </p>
          </div>
        </div>

        {timeUntil && (
          <div className={`px-3 py-2 rounded-lg ${isToday() ? 'bg-blue-500 text-white' : `${theme.tag} ${theme.text}`} font-semibold text-sm`}>
            {timeUntil}
          </div>
        )}
      </div>

      {/* Session Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-blue-600 dark:text-blue-400" />
          <div className={`font-semibold ${theme.text}`}>{session.time}</div>
        </div>
        <div className="flex items-center gap-2">
          <MapPin size={18} className="text-green-600 dark:text-green-400" />
          <div className={`font-semibold ${theme.text}`}>{session.facility}</div>
        </div>
        <div className="flex items-center gap-2">
          <Users size={18} className="text-purple-600 dark:text-purple-400" />
          <div className={`font-semibold ${theme.text}`}>{classData.enrolled} students</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3">
        {session.status === 'completed' ? (
          <div className="flex items-center gap-2 text-green-600 font-semibold">
            <CheckCircle size={20} />
            <span>Attendance Recorded</span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={onMarkAttendance}
                className={`flex items-center justify-center gap-2 px-4 py-3 ${theme.buttonPrimary} rounded-lg font-semibold`}
              >
                <CheckCircle size={20} />
                Mark Manually
              </button>
              <button
                onClick={onQRAttendance}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
              >
                <QrCode size={20} />
                QR Check-In
              </button>
            </div>
            <button
              className="flex items-center justify-center gap-2 px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <Play size={20} />
              Start Class
            </button>
          </>
        )}
      </div>

      {/* Quick Stats */}
      {session.attendees !== undefined && session.attendees > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm mb-2">
            <span className={theme.textSecondary}>Checked In</span>
            <span className="font-semibold">{session.attendees}/{classData.enrolled}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full"
              style={{ width: `${(session.attendees / classData.enrolled) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NextClassCard;
